/* Các ô nhập dùng chung cho trang /settings (cấp module để không mất focus). */
import type { ReactNode } from "react"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"

export type Guardian = { relationship: "father" | "mother"; full_name: string; email: string; phone_zalo: string; area: string }
export type LearnerProfile = { date_of_birth: string; gender: string; province_city: string; grade_level: string; grade_as_of_date: string; school_name: string }
export type SocialProfile = { favorite_food: string; favorite_movie: string; favorite_music: string; bio: string }

export const emptyGuardian = (relationship: "father" | "mother"): Guardian => ({ relationship, full_name: "", email: "", phone_zalo: "", area: "" })
export const emptyProfile: LearnerProfile = { date_of_birth: "", gender: "", province_city: "", grade_level: "", grade_as_of_date: "2026-10-01", school_name: "" }
export const emptySocial: SocialProfile = { favorite_food: "", favorite_movie: "", favorite_music: "", bio: "" }

export const GENDERS = [
  { value: "male", label: "Nam" },
  { value: "female", label: "Nữ" },
  { value: "other", label: "Khác" },
  { value: "prefer_not_to_say", label: "Không muốn trả lời" },
]
export const GRADES = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `Lớp ${i + 1}` }))
export const PROVINCES = ["Hà Nội", "Hồ Chí Minh", "Đà Nẵng", "Hải Phòng", "Cần Thơ", "Huế"]

export function Label({ htmlFor, children, optional }: { htmlFor?: string; children: ReactNode; optional?: boolean }) {
  return (
    <FieldLabel htmlFor={htmlFor}>
      {children}
      {optional ? (
        <span className="font-normal text-muted-foreground">(không bắt buộc)</span>
      ) : (
        <span className="text-orange" aria-label="bắt buộc">*</span>
      )}
    </FieldLabel>
  )
}

export function TextField({
  id, label, value, onChange, hint, className, ...rest
}: {
  id: string
  label: ReactNode
  value: string
  onChange: (v: string) => void
  hint?: ReactNode
  className?: string
} & Omit<React.ComponentProps<"input">, "value" | "onChange" | "id">) {
  return (
    <Field className={className}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} required {...rest} />
      {hint && <FieldDescription>{hint}</FieldDescription>}
    </Field>
  )
}

export function SelectField({
  id, label, value, onChange, items, placeholder, disabled, invalid,
}: {
  id: string
  label: ReactNode
  value: string
  onChange: (v: string) => void
  items: { value: string; label: string }[]
  placeholder: string
  disabled?: boolean
  invalid?: boolean
}) {
  return (
    <Field>
      <Label htmlFor={id}>{label}</Label>
      <Select items={items} value={value || null} onValueChange={(v) => onChange(String(v ?? ""))} disabled={disabled}>
        <SelectTrigger id={id} aria-invalid={invalid || undefined} className={cn("w-full min-w-0")}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {items.map((i) => (
            <SelectItem key={i.value} value={i.value}>
              {i.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}
